import './App.scss'
import 'primereact/resources/primereact.css'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'
import 'prismjs/themes/prism-coy.css'
import './assets/layout/layout.scss'
import 'bootstrap-icons/font/bootstrap-icons.min.css'

import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router'
import AppMainView from './views/AppMainView'
import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './store/hooks'
import { logout, selectAuthenticatedUser } from './store/auth/authSlice'
import LoginViewV2 from './views/auth/LoginViewV2'
import SignUpViewV2 from './views/auth/SignUpViewV2'
import WaitListView from './views/auth/WaitListView'
import SignUpVerificationViewV2 from './views/auth/SignUpVerificationViewV2'
import { OnboardingView } from './views/onboarding/OnboardingView'
import ResetPasswordViewV2 from './views/auth/ResetPasswordViewV2'
import NotFoundView from './views/NotFoundView'
import { IntercomProvider } from 'react-use-intercom'
import { refreshToken } from './store/auth/authActions'
import { readToken, Token } from './store/token/token'
import { useCompanySso } from './common/companySso'

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  )
}

function AppRoutes() {
  const defaultScale = 12
  const navigate = useNavigate()
  const location = useLocation()
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const dispatch = useAppDispatch()
  const { handleCompanySpecificSubdomain } = useCompanySso()

  useEffect(() => {
    document.documentElement.style.fontSize = defaultScale.toString() + 'px'
  }, [])

  const calculateTokenExpirationTimeLeft = (tokenExpiration: number): number => {
    const now = new Date().getTime()
    return Math.max(tokenExpiration * 1000 - now - 5, 0)
  }

  useEffect(() => {
    if (!authenticatedUser) {
      return
    }
    const token = Token.fromSerialized(authenticatedUser.token)
    const tokenExpirationTimeout = setTimeout(() => {
      if (token.isRenewable) {
        dispatch(refreshToken({ accountId: token.accountId, refreshToken: token.refreshToken! }))
      } else {
        dispatch(logout(location))
        navigate('/login')
      }
    }, calculateTokenExpirationTimeLeft(token.accessTokenExpiration))
    return () => {
      clearTimeout(tokenExpirationTimeout)
    }
  }, [authenticatedUser, dispatch, location, navigate])

  useEffect(() => {
    if (authenticatedUser) {
      return
    }
    const token = readToken()
    if (token) {
      dispatch(refreshToken({ accountId: token.accountId, refreshToken: token.refreshToken! }))
    }
  }, [authenticatedUser, dispatch])

  handleCompanySpecificSubdomain()

  return (
    <Routes>
      <Route element={<AppMainView />}>
        <Route index={true} element={<Navigate to={`/login${location.search}`} replace={true} />} />
        <Route path='/login' element={<LoginViewV2 />} />
        <Route path='/signup' element={<SignUpViewV2 />} />
        <Route path='/reset-password' element={<ResetPasswordViewV2 />} />
        <Route path='/verify' element={<SignUpVerificationViewV2 />} />
        <Route path='/wait-list' element={<WaitListView />} />
        <Route
          path='/onboarding'
          element={
            <IntercomProvider appId={import.meta.env.REACT_APP_INTERCOM_APPID!}>
              <OnboardingView />
            </IntercomProvider>
          }
        />
        <Route path='*' element={<NotFoundView />} />
      </Route>
    </Routes>
  )
}

export default App
